import { useState } from 'react'
import { Search, Download, BarChart3, X, Filter } from 'lucide-react'
import { useApp } from '../context/AppContext'
import { getStatusColor, formatDate } from '../utils/helpers'

export default function ReviewLibrary() {
  const { reviews, agents, prompts, knowledgeBases } = useApp()
  const [search, setSearch] = useState('')
  const [filterAdopted, setFilterAdopted] = useState('')
  const [filterAgent, setFilterAgent] = useState('')
  const [filterPrompt, setFilterPrompt] = useState('')
  const [showDetail, setShowDetail] = useState(null)

  const filtered = reviews.filter(r => {
    const matchSearch = r.taskName.toLowerCase().includes(search.toLowerCase())
    const matchAdopted = filterAdopted === '' || r.adopted === filterAdopted
    const matchAgent = !filterAgent || r.agentId === filterAgent
    const matchPrompt = !filterPrompt || r.promptVersion === filterPrompt
    return matchSearch && matchAdopted && matchAgent && matchPrompt
  })

  const exportCSV = () => {
    const headers = ['任务名称','任务类型','工作流','Step','Agent','Prompt版本','知识库版本','模型','采纳状态','失败原因','CTR','CVR','ACOS','TACOS','Orders','Sales','复盘结论','反哺Prompt','反哺知识库','生成新规则','负责人','日期']
    const rows = filtered.map(r => [
      r.taskName, r.taskType, agents.find(a=>a.id===r.workflow)?.name||'-', r.step,
      agents.find(a=>a.id===r.agentId)?.name||'-', r.promptVersion, r.knowledgeVersion, r.model,
      r.adopted, r.failureReason||'',
      r.metrics?.ctr||'-', r.metrics?.cvr||'-', r.metrics?.acos||'-', r.metrics?.tacos||'-',
      r.metrics?.orders||'-', r.metrics?.sales||'-',
      r.conclusion, r.feedbackPrompt?'是':'否', r.feedbackKnowledge?'是':'否', r.generateNewRule?'是':'否',
      r.owner, r.date
    ])
    const csv = [headers.join(','), ...rows.map(r => r.map(v => `"${String(v).replace(/"/g,'""')}"`).join(','))].join('\n')
    const blob = new Blob(['\ufeff'+csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob); const a = document.createElement('a')
    a.href = url; a.download = `review-export-${new Date().toISOString().split('T')[0]}.csv`; a.click()
  }

  const agentOptions = [...new Set(reviews.map(r => r.agentId))].filter(Boolean)
  const promptOptions = [...new Set(reviews.map(r => r.promptVersion))].filter(Boolean)

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-slate-800">结果复盘库</h2>
        <button onClick={exportCSV} className="btn-secondary flex items-center gap-2"><Download size={16} /> 导出 CSV</button>
      </div>

      <div className="card p-4">
        <div className="flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input className="input pl-9" placeholder="搜索任务..." value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <select className="select w-32" value={filterAdopted} onChange={e => setFilterAdopted(e.target.value)}>
            <option value="">全部采纳状态</option>
            <option value="采纳">已采纳</option>
            <option value="部分采纳">部分采纳</option>
            <option value="未采纳">未采纳</option>
          </select>
          <select className="select w-36" value={filterAgent} onChange={e => setFilterAgent(e.target.value)}>
            <option value="">全部 Agent</option>
            {agentOptions.map(id => <option key={id} value={id}>{agents.find(a=>a.id===id)?.name || id}</option>)}
          </select>
          <select className="select w-36" value={filterPrompt} onChange={e => setFilterPrompt(e.target.value)}>
            <option value="">全部 Prompt 版本</option>
            {promptOptions.map(v => <option key={v} value={v}>{v}</option>)}
          </select>
        </div>
      </div>

      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left px-4 py-3 font-medium">任务</th>
                <th className="text-left px-4 py-3 font-medium">Agent</th>
                <th className="text-left px-4 py-3 font-medium">Prompt</th>
                <th className="text-left px-4 py-3 font-medium">模型</th>
                <th className="text-left px-4 py-3 font-medium">采纳</th>
                <th className="text-left px-4 py-3 font-medium">ACOS</th>
                <th className="text-left px-4 py-3 font-medium">Orders</th>
                <th className="text-left px-4 py-3 font-medium">Sales</th>
                <th className="text-left px-4 py-3 font-medium">复盘结论</th>
                <th className="text-left px-4 py-3 font-medium">反哺</th>
                <th className="text-left px-4 py-3 font-medium">日期</th>
                <th className="text-left px-4 py-3 font-medium">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map(review => (
                <tr key={review.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3 font-medium text-slate-800">{review.taskName}</td>
                  <td className="px-4 py-3 text-slate-600">{agents.find(a=>a.id===review.agentId)?.name||'-'}</td>
                  <td className="px-4 py-3 text-slate-600">{review.promptVersion}</td>
                  <td className="px-4 py-3 text-slate-600">{review.model}</td>
                  <td className="px-4 py-3"><span className={`badge ${getStatusColor(review.adopted)}`}>{review.adopted}</span></td>
                  <td className="px-4 py-3 text-slate-600">{review.metrics?.acos||'-'}</td>
                  <td className="px-4 py-3 text-slate-600">{review.metrics?.orders||'-'}</td>
                  <td className="px-4 py-3 text-slate-600">{review.metrics?.sales ? `$${review.metrics.sales}` : '-'}</td>
                  <td className="px-4 py-3 text-slate-600 max-w-[200px] truncate">{review.conclusion}</td>
                  <td className="px-4 py-3 text-xs text-slate-500">
                    {review.feedbackPrompt && <span className="text-emerald-600">Prompt </span>}
                    {review.feedbackKnowledge && <span className="text-emerald-600">知识库 </span>}
                    {review.generateNewRule && <span className="text-purple-600">新规则</span>}
                  </td>
                  <td className="px-4 py-3 text-slate-500">{review.date}</td>
                  <td className="px-4 py-3"><button onClick={() => setShowDetail(review)} className="text-primary-600 hover:text-primary-700 text-xs">详情</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filtered.length === 0 && <div className="p-8 text-center text-slate-400">暂无数据</div>}
      </div>

      {showDetail && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between">
              <h3 className="text-lg font-bold">复盘详情: {showDetail.taskName}</h3>
              <button onClick={() => setShowDetail(null)} className="p-1 hover:bg-slate-100 rounded"><X size={18} /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="p-3 bg-slate-50 rounded-lg"><div className="text-slate-500 mb-1">Agent</div><div className="font-medium">{agents.find(a=>a.id===showDetail.agentId)?.name||'-'}</div></div>
                <div className="p-3 bg-slate-50 rounded-lg"><div className="text-slate-500 mb-1">模型</div><div className="font-medium">{showDetail.model}</div></div>
                <div className="p-3 bg-slate-50 rounded-lg"><div className="text-slate-500 mb-1">采纳状态</div><div className="font-medium">{showDetail.adopted}</div></div>
                {showDetail.failureReason && <div className="p-3 bg-red-50 rounded-lg"><div className="text-slate-500 mb-1">失败原因</div><div className="font-medium text-red-700">{showDetail.failureReason}</div></div>}
              </div>
              <div className="grid grid-cols-7 gap-3">
                {['ctr','cvr','acos','tacos','unitSession','orders','sales'].map(key => (
                  <div key={key} className="p-3 bg-slate-50 rounded-lg text-center">
                    <div className="text-xs text-slate-500 uppercase">{key}</div>
                    <div className="text-lg font-bold text-slate-800">{showDetail.metrics?.[key] || '-'}</div>
                  </div>
                ))}
              </div>
              <div><div className="text-sm font-medium text-slate-700 mb-1">输入摘要</div><div className="bg-slate-50 rounded-lg p-3 text-sm text-slate-600 whitespace-pre-wrap">{showDetail.inputSummary}</div></div>
              <div><div className="text-sm font-medium text-slate-700 mb-1">AI 输出</div><div className="bg-slate-50 rounded-lg p-3 text-sm text-slate-600 whitespace-pre-wrap">{showDetail.aiOutput}</div></div>
              <div><div className="text-sm font-medium text-slate-700 mb-1">人工修改版</div><div className="bg-slate-50 rounded-lg p-3 text-sm text-slate-600 whitespace-pre-wrap">{showDetail.humanEdit||'-'}</div></div>
              <div><div className="text-sm font-medium text-slate-700 mb-1">复盘结论</div><div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm text-slate-700">{showDetail.conclusion}</div></div>
              <div className="flex gap-4 text-sm">
                <div>反哺 Prompt: {showDetail.feedbackPrompt ? <span className="text-emerald-600 font-medium">是</span> : '否'}</div>
                <div>反哺知识库: {showDetail.feedbackKnowledge ? <span className="text-emerald-600 font-medium">是</span> : '否'}</div>
                <div>生成新规则: {showDetail.generateNewRule ? <span className="text-purple-600 font-medium">是</span> : '否'}</div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
